/**
 * The metrics, as charts.
 *
 * ===========================================================================
 * WHAT `talk_pct` IS, BECAUSE GETTING THIS WRONG IS A LIE RATHER THAN A TYPO
 *
 * The spec now defines it: **"Share of all participants' speaking time, not
 * share of call duration. Per-speaker values sum to 100."** So a 45/55 split
 * says how the speech was divided between two people and says NOTHING about
 * how much of the call was speech. On the reference session those bars read
 * 24.1% and 75.9% — of 2:40 of speech inside a 4:26 call, 48.1% of which was
 * silence. Presented as "share of the call", the same two bars describe a call
 * that was fully occupied by talking, which is the opposite of what happened.
 *
 * Two rules follow, and both are enforced below:
 *
 *   1. every talk-share label names its denominator as SPEECH, never the call;
 *   2. `silence_pct` — "share of the session timeline with no active speaker"
 *      — is drawn beside it, because the honest picture needs both. It is the
 *      only field here measured against the timeline, which is exactly why it
 *      is the one that can put the shares in proportion.
 *
 * The total speech figure comes from `silence_pct` and the call duration, NOT
 * from `sum(talk_seconds)`: talk seconds are per speaker and overlapping
 * speech is counted once for each person in it, so on that same session they
 * sum to 2:40 while the timeline was only occupied for 2:18. Summing them and
 * calling the result "time anyone was speaking" over-counts every interruption
 * in the call.
 * ===========================================================================
 *
 * `SessionMetrics` is nine fields and it would fit in a definition list — but a
 * definition list of nine numbers is the thing everybody skips. Each of these
 * answers a question somebody actually has after a conversation, and the form
 * is picked per question rather than applied uniformly:
 *
 *   who talked        → bars. The interesting bit is 45 vs 55, and only a
 *                       shared baseline makes that legible at a glance.
 *   how it ran        → ticks in time order. A count of turns says how many; a
 *                       strip says whether it was a dialogue or a monologue
 *                       with interruptions.
 *   turns / pace /
 *   overlaps / latency→ stat tiles. A single number is not improved by being
 *                       drawn as a single bar.
 *   silence           → a meter. It is a proportion of one whole.
 *
 * Every number here is served straight from `SessionMetrics`. Nothing is
 * recomputed client-side, so this screen and any other consumer of the API
 * agree by construction.
 */

import type { ConversationMetrics, TranscriptDocument } from '@era/shared'
import { formatDuration } from '@/lib/format'
import type { SpeakerRoster } from '../speakers'
import type { Player } from '../use-player'
import { BarRows, Meter, StatTile, TurnTicks, type BarRow } from './charts'
import { Timeline, type TimelineLane } from './timeline'

export function InsightsPanel({
  metrics,
  roster,
  transcript,
  lanes,
  player,
  timelineDurationMs,
  roomDurationMs,
}: {
  metrics: ConversationMetrics
  roster: SpeakerRoster
  transcript: TranscriptDocument | null
  lanes: TimelineLane[]
  player: Player
  /**
   * The SESSION TIMELINE's length — the whole that `silence_pct` is a share of.
   *
   * MEASURED, and it is not `SessionDetailView.duration_ms`. The spec defines
   * `silence_pct` as "share of the session timeline with no active speaker",
   * and `ArtifactTimebase.session` as "shares the mixed playback timeline" — so
   * the denominator is the length of the mixed recording, which starts at the
   * first published audio, not when the room opened. On the verification call
   * `s_20260808194008_C9m8ZrZrMb` those were 1:17 and 2:44: the room sat empty
   * for 68 seconds before anyone published. Labelling 9.1% with 2:44 next to it
   * claimed 15 seconds of speech where there were 7 — the same wrong-denominator
   * bug this card was rewritten to fix, one level up.
   *
   * Not the player's `durationMs` either: that is 0 until the audio has loaded,
   * so the sentence would change under the reader.
   */
  timelineDurationMs: number | null
  /**
   * How long the room was open. Shown only when it is materially longer than
   * the timeline, because that gap is otherwise unexplained: the header says
   * 2:44 and this card says 1:17, and a reader with no sentence for the
   * difference assumes one of them is wrong.
   */
  roomDurationMs: number | null
}) {
  // Driven by the roster, not by `Object.keys(talk_pct)`: the roster fixes both
  // the order and the colour, so a speaker who said nothing still appears — at
  // zero, which is itself the answer to "did everyone speak?".
  const speakers = roster.list.length > 0 ? roster.list : []

  const talkRows: BarRow[] = speakers.map((speaker) => ({
    key: speaker.identity,
    label: speaker.displayName,
    value: metrics.talk_pct[speaker.identity] ?? 0,
    display: `${round(metrics.talk_pct[speaker.identity] ?? 0)}%`,
    color: speaker.color,
  }))

  const monologueRows: BarRow[] = speakers.map((speaker) => ({
    key: speaker.identity,
    label: speaker.displayName,
    value: metrics.longest_monologue_s[speaker.identity] ?? 0,
    display: formatDuration((metrics.longest_monologue_s[speaker.identity] ?? 0) * 1000),
    color: speaker.color,
  }))

  const questionRows: BarRow[] = speakers.map((speaker) => ({
    key: speaker.identity,
    label: speaker.displayName,
    value: metrics.questions[speaker.identity] ?? 0,
    display: String(metrics.questions[speaker.identity] ?? 0),
    color: speaker.color,
  }))

  // The sum of the per-speaker figures, and labelled as exactly that. It is
  // NOT the time the call was occupied by speech — overlapping speech is in it
  // once per speaker. It is the right denominator for `talk_pct` (which is why
  // the bars sum to 100 against it) and the wrong one for anything about the
  // timeline.
  const speechSecondsTotal = speakers.reduce(
    (total, speaker) => total + (metrics.talk_seconds[speaker.identity] ?? 0),
    0,
  )

  // The timeline half of the picture. `silence_pct` is the only metric here
  // measured against the session timeline, so its complement is the only
  // honest answer to "how much of this call was anybody talking".
  const silencePct = metrics.silence_pct
  const occupiedPct = silencePct === null ? null : Math.max(0, 100 - silencePct)

  // Five seconds, not zero: the two numbers come from different clocks upstream
  // and a second of rounding between them is not a fact worth a sentence. Sixty
  // eight seconds is.
  const roomLeadIn =
    roomDurationMs !== null &&
    timelineDurationMs !== null &&
    roomDurationMs - timelineDurationMs > 5_000
      ? roomDurationMs
      : null

  const ticks =
    transcript?.turns.map((turn, index) => {
      const speaker = roster.get(turn.identity)
      return {
        key: `${turn.start_ms}-${index}`,
        color: speaker.color,
        startMs: turn.start_ms,
        widthRatio: Math.max(turn.end_ms - turn.start_ms, 1),
        label: `${speaker.displayName} · ${formatDuration(turn.start_ms)} · ${formatDuration(turn.end_ms - turn.start_ms)}`,
      }
    }) ?? []

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* -- who talked --------------------------------------------------- */}
        <section className="rounded-xl border border-line bg-surface px-4 py-4 sm:px-5 sm:py-5">
          <header className="mb-4 space-y-1">
            <h3 className="text-sm font-semibold text-ink">
              How the {formatDuration(speechSecondsTotal * 1000)} of speech was divided
            </h3>
            <p className="text-xs leading-relaxed text-ink-muted">
              Share of speaking time, not of the call — these bars sum to 100% however much of the
              call was silence.
            </p>
          </header>
          <BarRows rows={talkRows} max={100} />
          <p className="mt-4 border-t border-line pt-3 text-[0.6875rem] text-ink-dim">
            {speakers
              .map(
                (speaker) =>
                  `${speaker.displayName} ${formatDuration((metrics.talk_seconds[speaker.identity] ?? 0) * 1000)}`,
              )
              .join(' · ')}
          </p>

          {/* -- and how much of the call that was ---------------------------- */}
          {/*
            The second half of the same fact, in the same card, because
            separating them is what makes the first half misleading. A 45/55
            split above a bar showing that 48% of the call was nobody speaking
            is a conversation; the same 45/55 alone reads as a full call.

            Drawn from `silence_pct` — a share of the SESSION TIMELINE — rather
            than from the speech total above it, which double-counts overlap.
            Absent rather than approximated when upstream did not compute it.
          */}
          {occupiedPct !== null ? (
            <div className="mt-4 border-t border-line pt-3">
              <div className="flex items-baseline justify-between gap-3">
                <p className="eyebrow">of the recorded timeline</p>
                <p className="text-[0.6875rem] tabular text-ink-dim">
                  {formatDuration(timelineDurationMs)} long
                </p>
              </div>
              {/* One bar, two shares of one whole: speech from the left,
                  silence taking the rest. The speech share is the accent —
                  it is the part the bars above are a breakdown OF. */}
              <div
                className="mt-2 flex h-2 w-full overflow-hidden rounded-sm bg-surface-2"
                role="img"
                aria-label={`${round(occupiedPct)}% of the call had someone speaking, ${round(
                  silencePct ?? 0,
                )}% was silence`}
              >
                <span
                  className="block h-full bg-accent"
                  style={{ width: `${Math.min(100, occupiedPct)}%` }}
                />
              </div>
              <p className="mt-2 text-[0.6875rem] leading-relaxed text-ink-dim">
                Someone was speaking for {round(occupiedPct)}% of it; {round(silencePct ?? 0)}% was
                silence. Overlaps make the speech total above longer than the time the call was
                occupied — two people talking at once is counted for both of them.
                {roomLeadIn !== null ? (
                  <>
                    {' '}
                    The room was open {formatDuration(roomLeadIn)}; this timeline is the recording,
                    which starts at the first audio anyone published.
                  </>
                ) : null}
              </p>
            </div>
          ) : null}
        </section>

        {/* -- how it ran ---------------------------------------------------- */}
        {/*
          THE TICK STRIP DOES NOT SURVIVE 375px, and not because it is drawn
          badly — because of arithmetic. Every tick carries `min-w-[3px]` so
          that a 300 ms "mm-hm" in a long call stays visible, plus a 2px gap; at
          96 turns that is a 480px floor, and this session had 96 turns in 42
          minutes. A real coaching hour runs to three or four hundred. Past the
          container's width the flex row simply pushes out of it, so what a
          phone would show is not a squeezed strip, it is the first two thirds
          of the conversation and no indication that the rest exists. Clipping
          it would be worse: silently truncated data that still looks complete.

          So below `sm` it is replaced by the ribbon that was already underneath
          it, which answers the same question — what was the shape of this
          conversation, who held the floor and when — at any turn count, because
          it is drawn in TIME rather than in turns and time does not run out of
          horizontal room. What is genuinely lost is rhythm independent of
          silence: fourteen quick exchanges and fourteen slow ones look the
          same on the ribbon. The turn count and the switches-per-minute figure
          below it are the numeric form of exactly that, and they are kept on
          both.
        */}
        <section className="flex flex-col rounded-xl border border-line bg-surface px-4 py-4 sm:px-5 sm:py-5">
          <header className="mb-4 space-y-1">
            <h3 className="text-sm font-semibold text-ink">How the conversation ran</h3>
            <p className="text-xs leading-relaxed text-ink-muted">
              <span className="sm:hidden">
                Who held the floor, against the clock. Tap or drag to play from anywhere.
              </span>
              <span className="hidden sm:inline">
                Every turn in order, width by length. Click one to play it.
              </span>
            </p>
          </header>

          {ticks.length > 0 ? (
            <>
              <div className="hidden max-w-full overflow-x-auto pb-1 sm:block">
                <TurnTicks ticks={ticks} onSelect={(ms) => player.seekMs(ms, { play: true })} />
              </div>
              <p className="text-[0.6875rem] text-ink-dim sm:mt-2">
                {ticks.length} {ticks.length === 1 ? 'turn' : 'turns'}
                {metrics.switches_per_min !== null
                  ? ` · the floor changed hands ${round(metrics.switches_per_min)} times a minute`
                  : ''}
              </p>
            </>
          ) : (
            <div className="rounded-md border border-dashed border-line px-4 py-6 text-center text-xs text-ink-dim">
              Turn order comes from the transcript, which is not ready yet.
            </div>
          )}

          {/* The same ribbon as the player's, at the same scale and driven by the
              same clock — so "when in the call" means the same thing in both
              places. Where the strip above is turns, this is speech: the gaps
              between the bars are the silence the tile below counts. */}
          <div className="mt-auto pt-4 sm:pt-5">
            {/* Below `sm` the ribbon IS the section — the tick strip above it
                is gone — so a second label saying what the section already says
                is noise. */}
            <p className="eyebrow mb-2 max-sm:hidden">speech against the clock</p>
            <Timeline
              player={player}
              lanes={lanes}
              durationMs={player.durationMs}
              laneHeight={8}
              className="[--era-lane:7px] sm:[--era-lane:initial]"
            />
          </div>
        </section>
      </div>

      {/* -- the four numbers ----------------------------------------------- */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="turns taken"
          value={metrics.turn_count ?? '—'}
          caption="A turn is one uninterrupted stretch by one person."
        />
        <StatTile
          label="speaker changes"
          value={metrics.switches_per_min !== null ? round(metrics.switches_per_min) : '—'}
          unit="per minute"
          caption="How often the floor changed hands. Low is a briefing; high is a conversation."
        />
        <StatTile
          label="overlaps"
          value={metrics.overlap_events ?? '—'}
          caption="Moments when two people were speaking at once."
        />
        <StatTile
          label="response latency"
          value={
            metrics.mean_response_latency_ms !== null
              ? round(metrics.mean_response_latency_ms / 1000, 2)
              : '—'
          }
          unit={metrics.mean_response_latency_ms !== null ? 'seconds, mean' : undefined}
          tone={metrics.mean_response_latency_ms === null ? 'absent' : 'default'}
          caption={
            metrics.mean_response_latency_ms !== null
              ? 'Average gap between one person stopping and the next starting.'
              : 'Not measurable here — it needs turn transitions the pipeline did not find.'
          }
        />
      </div>

      {/* -- silence, monologues, questions ---------------------------------- */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* The unit is the definition, spelled out: "share of the session
            timeline with no active speaker". Not "% of the call" — which is
            the same thing here but is also what the talk shares above are NOT,
            and having the two read as the same kind of percentage is precisely
            how a reader concludes the bars are shares of the call. */}
        <StatTile
          label="silence"
          value={metrics.silence_pct !== null ? round(metrics.silence_pct) : '—'}
          unit="% of the timeline"
          caption="The share of the call with no active speaker. Silence is not dead air — it is thinking time, and a coaching conversation with none of it is usually one person lecturing."
        >
          <div className="pt-1">
            <Meter ratio={(metrics.silence_pct ?? 0) / 100} color="var(--accent)" />
          </div>
        </StatTile>

        <section className="rounded-xl border border-line bg-surface px-4 py-4 sm:px-5">
          <p className="eyebrow mb-3">longest unbroken stretch</p>
          <BarRows rows={monologueRows} />
          <p className="mt-3 text-[0.6875rem] leading-relaxed text-ink-dim">
            The longest a single person held the floor without a break.
          </p>
        </section>

        <section className="rounded-xl border border-line bg-surface px-4 py-4 sm:px-5">
          <p className="eyebrow mb-3">questions asked</p>
          <BarRows
            rows={questionRows}
            emptyNote="The engine found no questions in this transcript."
          />
          <p className="mt-3 text-[0.6875rem] leading-relaxed text-ink-dim">
            Detected from the transcript, so it counts phrasing rather than intent.
          </p>
        </section>
      </div>
    </div>
  )
}

function round(value: number, places = 1): string {
  const factor = 10 ** places
  const rounded = Math.round(value * factor) / factor
  return String(rounded)
}
